// SavedDraftsView.tsx - Enterprise table design for saved drafts
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './GatePassForm.css';
import '../EnterpriseTable.css';
import { deleteDraft, submitDraft } from './draftService';


interface Draft {
  draftId: number;
  draftName: string;
  completionStatus: {
    completionPercentage: number;
    isSubmittable: boolean;
    completion: {
      basicInfo: boolean;
      requestDetails: boolean;
      approvers: boolean;
      gates: boolean;
      personnel: boolean;
      validDateRange: boolean;
    };
  };
  isSubmittable: boolean;
  gateCount: number;
  personnelCount: number;
  gateNumbers: string;
  originalRequestId?: number | null;
  isRevision?: boolean;
  lastModified: string;
  createdAt: string;
}

interface StatusMessage {
  type: 'success' | 'error' | null;
  message: string;
}

interface SavedDraftsViewProps {
  getAccessToken: () => Promise<string>;
  onStatusUpdate: (status: StatusMessage) => void;
  onEditDraft?: (draftId: number) => void;
  onCreateNew?: () => void;
}
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

export const SavedDraftsView: React.FC<SavedDraftsViewProps> = ({
  getAccessToken,
  onStatusUpdate,
  onEditDraft,
  onCreateNew
}) => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'lastModified' | 'draftName' | 'completion'>('lastModified');
  const [filterBy, setFilterBy] = useState<'all' | 'ready' | 'incomplete'>('all');
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [authError, setAuthError] = useState<string | null>(null);


  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

const loadDrafts = useCallback(async (): Promise<void> => {
    try {
        setIsLoading(true);
        setAuthError(null);  // ✅ Reset on each load attempt

        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('No access token available');

        const authHeader = accessToken.startsWith('Bearer ')
            ? accessToken
            : `Bearer ${accessToken}`;

       const response = await fetch(
    `${API_BASE_URL}/listDrafts`,
    {
        method: 'GET',
        headers: {
            Authorization: authHeader,
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
    }
);

        // ✅ FIX: Treat 401 as an auth problem, not a generic load failure
        if (response.status === 401) {
            setAuthError('Your session has expired. Please sign out and sign in again.');
            return;
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API request failed: ${response.status} - ${errorText}`);
        }

        const responseText = await response.text();
        const result = JSON.parse(responseText);

        if (result.success) {
            setDrafts(result.data || []);
        } else {
            // ✅ FIX: If the API still returns success:false with an auth message, treat it as auth
            const msg: string = result.message || result.error || 'API returned error';
            if (msg.toLowerCase().includes('authentication') || msg.toLowerCase().includes('sign')) {
                setAuthError(msg);
            } else {
                throw new Error(msg);
            }
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        onStatusUpdate({
            type: 'error',
            message: `Failed to load saved drafts: ${errorMessage}`,
        });
    } finally {
        setIsLoading(false);
    }
}, [getAccessToken, onStatusUpdate]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const handleDeleteDraft = async (draftId: number) => {
    try {
      setIsDeleting(draftId);
      await deleteDraft(draftId, getAccessToken);
      setDrafts(prev => prev.filter(draft => draft.draftId !== draftId));
      setShowDeleteConfirm(null);
      onStatusUpdate({ type: 'success', message: 'Draft deleted successfully' });
    } catch (error) {
      onStatusUpdate({
        type: 'error',
        message: `Failed to delete draft: ${error instanceof Error ? error.message : 'Unknown error occurred'}`
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleSubmitDraft = async (draftId: number, draftName: string) => {
    try {
      setIsSubmitting(draftId);
      setOpenDropdown(null);
      const result = await submitDraft(draftId, getAccessToken);
      if (result.success) {
        setDrafts(prev => prev.filter(draft => draft.draftId !== draftId));
        onStatusUpdate({
          type: 'success',
          message: `Draft "${draftName}" submitted successfully! Request ID: ${result.data.requestId}`
        });
      }
    } catch (error) {
      onStatusUpdate({
        type: 'error',
        message: `Failed to submit draft: ${error instanceof Error ? error.message : 'Unknown error occurred'}`
      });
    } finally {
      setIsSubmitting(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // const _formatDateTime = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit'
  //   });
  // };

  const getStatusDot = (draft: Draft): { label: string; dotColor: string } => {
    const completion = draft.completionStatus?.completionPercentage || 0;
    const isSubmittable = draft.completionStatus?.isSubmittable || draft.isSubmittable;

    if (isSubmittable) return { label: 'Ready', dotColor: '#10b981' };
    if (completion >= 75) return { label: 'Almost Ready', dotColor: '#f59e0b' };
    if (completion >= 50) return { label: 'In Progress', dotColor: '#3b82f6' };
    return { label: 'Started', dotColor: '#9ca3af' };
  };

  // Filter and sort
  const filteredAndSortedDrafts = drafts
    .filter(draft => {
      const matchesSearch = draft.draftName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        draft.gateNumbers.toLowerCase().includes(searchTerm.toLowerCase());
      if (filterBy === 'ready') return matchesSearch && (draft.completionStatus?.isSubmittable || draft.isSubmittable);
      if (filterBy === 'incomplete') return matchesSearch && !(draft.completionStatus?.isSubmittable || draft.isSubmittable);
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'draftName': return a.draftName.localeCompare(b.draftName);
        case 'completion':
          return (b.completionStatus?.completionPercentage || 0) - (a.completionStatus?.completionPercentage || 0);
        default:
          return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
      }
    });

  // Determine which view to render
  const renderContent = () => {

  // Loading
  if (isLoading) {
    return (
      <div className="company-management">
        <div className="companies-container">
          <div className="loading">
            <span className="spinner"></span>
            Loading drafts...
          </div>
        </div>
      </div>
    );
  }

  // After the isLoading block, add this before the main return:
if (authError) {
    return (
        <div style={{ padding: '24px' }}>
            <div style={{
                backgroundColor: '#fff7ed',
                border: '1px solid #fed7aa',
                borderRadius: '8px',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
            }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea580c"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <div>
                    <p style={{ margin: 0, color: '#9a3412', fontSize: '14px', fontWeight: 500 }}>
                        {authError}
                    </p>
                    <p style={{ margin: '4px 0 0', color: '#c2410c', fontSize: '13px' }}>
                        Sign out from the top menu and sign back in to continue.
                    </p>
                </div>
            </div>
        </div>
    );
}

  // Empty — no drafts at all
  if (drafts.length === 0) {
    return (
      <div className="company-management">
        <div className="companies-container">
          <div className="no-companies">
            <div className="no-data-icon">📋</div>
            <h3>No Saved Drafts</h3>
            <p>You haven't saved any drafts yet. Start creating a gate pass request to save your progress.</p>
            {onCreateNew && (
              <button onClick={onCreateNew} className="create-company-btn">
                Create New Request
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-management" style={{ background: '#ffffff' }}>
      <div className="companies-container">
        {/* Toolbar */}
        <div className="table-toolbar">
          <div className="toolbar-left">
            <span className="record-count">
              {filteredAndSortedDrafts.length} draft{filteredAndSortedDrafts.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="toolbar-right" style={{ gap: '10px' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search drafts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                style={{ paddingLeft: '32px' }}
              />
            </div>

            {/* Filter */}
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as any)}
              style={{
                height: '34px',
                padding: '0 28px 0 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                fontSize: '13px',
                color: '#242424',
                cursor: 'pointer',
                outline: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center'
              }}
            >
              <option value="all">All Drafts</option>
              <option value="ready">Ready</option>
              <option value="incomplete">Incomplete</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              style={{
                height: '34px',
                padding: '0 28px 0 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                fontSize: '13px',
                color: '#242424',
                cursor: 'pointer',
                outline: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center'
              }}
            >
              <option value="lastModified">Modified ↓</option>
              <option value="draftName">Name</option>
              <option value="completion">Completion</option>
            </select>

            {onCreateNew && (
              <button onClick={onCreateNew} className="create-company-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>New Request</span>
              </button>
            )}
          </div>
        </div>

        {/* Search results bar */}
        {searchTerm && (
          <div className="search-results-bar">
            <span>{filteredAndSortedDrafts.length} result{filteredAndSortedDrafts.length !== 1 ? 's' : ''} for "{searchTerm}"</span>
            <button className="clear-filter-btn" onClick={() => setSearchTerm('')}>Clear</button>
          </div>
        )}

        {/* Table */}
        <div className="companies-table-container">
          <table className="minimalist-table" style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                <th>DRAFT NAME</th>
                <th style={{ width: '120px' }}>STATUS</th>
                <th style={{ width: '90px' }}>PROGRESS</th>
                <th style={{ width: '180px' }}>DETAILS</th>
                <th style={{ width: '120px' }}>MODIFIED</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedDrafts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-results">
                    <div className="no-results-content">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <p>No drafts match the current filter</p>
                      <button onClick={() => { setFilterBy('all'); setSearchTerm(''); }} className="clear-search-link">Show all drafts</button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedDrafts.map((draft) => {
                  const status = getStatusDot(draft);
                  const completion = draft.completionStatus?.completionPercentage || 0;
                  const isSubmittable = draft.completionStatus?.isSubmittable || draft.isSubmittable;
                  const isReturnedDraft = draft.draftName?.startsWith('Returned:');

                  return (
                    <tr key={draft.draftId}>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                          <span
                            style={{
                              fontWeight: 600,
                              color: '#242424',
                              cursor: 'pointer',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block'
                            }}
                            title={draft.draftName}
                            onClick={() => onEditDraft?.(draft.draftId)}
                          >
                            {draft.draftName}
                          </span>
                          {isReturnedDraft && (
                            <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 500 }}>
                              Returned for revision{draft.originalRequestId ? ` · Request #${draft.originalRequestId}` : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, color: '#242424' }}>
                          <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: status.dotColor, flexShrink: 0 }} />
                          {status.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '40px',
                            height: '4px',
                            backgroundColor: '#f1f3f5',
                            borderRadius: '2px',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            <div style={{
                              width: `${completion}%`,
                              height: '100%',
                              backgroundColor: status.dotColor,
                              borderRadius: '2px'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>{completion}%</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>
                            {draft.gateCount} gate{draft.gateCount !== 1 ? 's' : ''} · {draft.personnelCount} personnel
                          </span>
                          {draft.gateNumbers && (
                            <span style={{ 
                              color: '#9ca3af', 
                              fontSize: '11px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '140px',
                              display: 'block'
                            }} title={draft.gateNumbers}>
                              {draft.gateNumbers}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span style={{ color: '#6b7280', fontSize: '13px' }}>
                          {formatDate(draft.lastModified)}
                        </span>
                      </td>
                      <td className="td-actions">
                        <div style={{ position: 'relative' }} ref={openDropdown === draft.draftId ? dropdownRef : null}>
                          <button
                            className="action-trigger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === draft.draftId ? null : draft.draftId);
                            }}
                            aria-label="Actions"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="3" r="1.5" fill="#6b7280" />
                              <circle cx="8" cy="8" r="1.5" fill="#6b7280" />
                              <circle cx="8" cy="13" r="1.5" fill="#6b7280" />
                            </svg>
                          </button>

                          {openDropdown === draft.draftId && (
                            <div className="dropdown-menu" style={{ right: 0, top: '100%', minWidth: '160px' }}>
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setOpenDropdown(null);
                                  onEditDraft?.(draft.draftId);
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                </svg>
                                Edit
                              </button>

                              {isSubmittable && !isReturnedDraft && (
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleSubmitDraft(draft.draftId, draft.draftName)}
                                  disabled={isSubmitting === draft.draftId}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                  </svg>
                                  {isSubmitting === draft.draftId ? 'Submitting...' : 'Submit'}
                                </button>
                              )}

                              <div className="dropdown-divider" />

                              <button
                                className="dropdown-item dropdown-item-danger"
                                onClick={() => {
                                  setOpenDropdown(null);
                                  setShowDeleteConfirm(draft.draftId);
                                }}
                                disabled={isDeleting === draft.draftId}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                                {isDeleting === draft.draftId ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="table-footer">
          <div className="table-stats">
            <span className="stat-item">
              Showing <strong>{filteredAndSortedDrafts.length}</strong> of <strong>{drafts.length}</strong> drafts
            </span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#242424', fontWeight: 600 }}>
              Delete Draft
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '14px', lineHeight: 1.5 }}>
              Are you sure you want to delete this draft? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  color: '#475569',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDraft(showDeleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  };

  return (
    <>
      <h1 style={{
        fontSize: '25px',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: '0 0 12px 0',
        padding: '5px 0 0 20px',
        lineHeight: 1.3,
        fontFamily: "'Segoe UI', sans-serif",
        letterSpacing: '-0.01em',
      }}>
        Saved Drafts & Returned Requests
      </h1>

      {renderContent()}
    </>
  );
};